import { Component, OnDestroy, OnInit } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { PolicySelectors } from '@amfam/policy/data-access';
import { AutoPolicy } from '@amfam/policy/models';
import { userQuery } from '@amfam/shared/user';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DsModalService,
  LoadingSpinnerService,
  PageTitleService,
  ToasterService
} from '@amfam/ui-kit';

import * as fromRoot from '../../../../core/store';
import {
  KydCustomerDataModel,
  KydSmartphonePolicy
} from '../../../../core/store/programs/kyd-smartphone/models/kyd-smartphone-policy.model';
import * as KydPartialHouseholdActions from '../../+state/kyd-partial-household.actions';
import { kydPartialHouseholdQuery } from '../../+state/kyd-partial-household.selectors';
import {
  EnrollKydOperatorPayload,
  EnrollKydPolicyData,
  UpdateKydOperatorPhonePayload
} from '../../models/kyd-partial-household.model';

@Component({
  selector: 'ds-kyd-partial-household',
  templateUrl: './kyd-partial-household.component.html',
  styleUrls: ['./kyd-partial-household.component.scss'],
  standalone: false
})
export class KydPartialHouseholdComponent implements OnInit, OnDestroy {
  public dataLoaded$ = this.store.select(fromRoot.getKydSmartphoneEnrollmentsLoaded);
  public dataLoading$: Observable<boolean>;
  public phhDataLoading$ = this.store.select(kydPartialHouseholdQuery.isKydPhhLoading);
  public isPhhEligible$ = this.store.select(fromRoot.isKydPartialHouseholdEligible);
  public eligiblePolicies$: Observable<KydSmartphonePolicy[]> = this.store.select(
    fromRoot.getKydSmartphoneEligiblePolicies
  );
  showKYDManageEnrollmentDuringMigration$: Observable<boolean>;
  isKydEnrolled$: Observable<boolean>;
  public enrollmentEmailId$ = this.store.pipe(
    select(userQuery.selectMyAccountEmail),
    withLatestFrom(this.store.select(userQuery.selectPrimaryEmail)),
    map(
      ([accountEmail, primaryEmail]) =>
        _get(primaryEmail, 'emailAddress', '') || _get(accountEmail, 'emailAddress', '')
    )
  );

  public phoneList$: Observable<string[]> = this.store.select(userQuery.selectPhoneWithAreaCode);

  public policyOperatorData$ = this.store.select(fromRoot.getPartialHouseHoldOperatorModelData);

  state$: Observable<string> = this.store.select(userQuery.selectUserStateCode);

  public eligibleForUtah$ = this.store.select(userQuery.selectUserStateCode).pipe(
    withLatestFrom(this.isPhhEligible$),
    map(([state, isEligible]) => {
      const states = this.copyService.getCopy('kydSmartphonePhh.kydmodal.states');
      return states.includes(state) && isEligible;
    })
  );
  public policyOperatorDataForUnEnroll$ = this.policyOperatorData$.pipe(
    withLatestFrom(this.eligibleForUtah$),
    map(([dataMap, eligible]) => (eligible ? Object.values(dataMap) : null))
  );

  headerContentRef$: Observable<string> = this.eligibleForUtah$.pipe(
    map(eligibleForUtah =>
      eligibleForUtah
        ? 'kydSmartphonePhh.kydmodal.unenrollAllHeader'
        : 'kydSmartphonePhh.unenrollHeader'
    )
  );

  bodyContentRef$: Observable<string> = this.eligibleForUtah$.pipe(
    withLatestFrom(this.state$),
    map(([isEligible, stateCode]) => {
      if (isEligible) {
        return 'kydSmartphonePhh.kydmodal.unenrollAllBody';
      } else if (stateCode === 'WA') {
        return 'kydSmartphonePhh.unenrollAllWA';
      } else {
        return 'kydSmartphonePhh.unenrollAllWA';
      }
    })
  );
  confirmContentRef$: Observable<string> = this.eligibleForUtah$.pipe(
    map(eligibleForUtah =>
      eligibleForUtah
        ? 'kydSmartphonePhh.kydmodal.confirmContent'
        : 'kydSmartphonePhh.unenrollAllConfirm'
    )
  );

  public isPolicyEnrolled$ = this.store.select(fromRoot.isKydSmartphonePolicyEnrolled);

  public hasMultipleOperatorsEnrolled$ = this.store.select(
    fromRoot.hasMultipleKydOperatorsEnrolled
  );

  public hasMultipleEligibleKydOperators$ = this.store.select(
    fromRoot.hasMultipleEligibleKydOperators
  );

  public policyState$ = this.store.select(fromRoot.getKydPolicyState);

  public selectedAutoPolicy$: Observable<AutoPolicy> = <Observable<AutoPolicy>>(
    this.eligiblePolicies$.pipe(
      map(policies => policies[0]),
      withLatestFrom(this.store.select(PolicySelectors.selectAdvanceAutoPolicies)),
      map(([policy, autoPolicies]) =>
        autoPolicies.find(
          autoPolicy => _get(autoPolicy, 'policyNumber') === _get(policy, 'policyNumber')
        )
      )
    )
  );
  public kydPolicy: KydSmartphonePolicy;
  public recentAction$ = this.store.select(kydPartialHouseholdQuery.getKydRecentAction);
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store<fromRoot.RootState>,
    private toasterService: ToasterService,
    private modalService: DsModalService,
    private spinner: LoadingSpinnerService,
    private pageTitle: PageTitleService,
    private copyService: CopyService
  ) {}

  ngOnInit() {
    this.spinner.start();
    this.store.dispatch(new KydPartialHouseholdActions.ResetState());
    this.pageTitle.set('KnowYourDrive Smartphone Program');

    this.isKydEnrolled$ = this.store.select(fromRoot.getKydSmartphoneEnrollments).pipe(
      takeUntil(this.stop$),
      map(summaries =>
        summaries.some(summary => {
          return (
            summary.enrollmentStatus === 'Enrolled' || summary.enrollmentStatus === 'Waiting Period'
          );
        })
      )
    );

    this.showKYDManageEnrollmentDuringMigration$ = combineLatest([
      this.store.select(fromRoot.isWithinKydMigrationPeriod),
      this.isKydEnrolled$
    ]).pipe(
      map(([withinMigrationPeriod, isKydEnrolled]) => withinMigrationPeriod && isKydEnrolled)
    );

    this.store
      .select(fromRoot.getKydSmartphonePolicyData)
      .pipe(takeUntil(this.stop$))
      .subscribe(policy => {
        policy = policy.filter(p => !!p);
        this.kydPolicy = policy[0];
      });

    this.dataLoading$ = combineLatest(
      this.store.select(fromRoot.getAnyKydLoading),
      this.phhDataLoading$,
      (phhLoading, anyKydLoading) => {
        return phhLoading || anyKydLoading;
      }
    );

    this.dataLoading$.pipe(takeUntil(this.stop$)).subscribe(loading => {
      if (loading) {
        this.spinner.start();
      } else {
        this.spinner.stop();
      }
    });

    this.store
      .select(kydPartialHouseholdQuery.getKydToastMessage)
      .pipe(
        takeUntil(this.stop$),
        filter(toast => !!toast.body)
      )
      .subscribe(toast => {
        this.toasterService.pop(toast.type, toast.body);
      });
  }

  /**
   *
   * @description Opens the Enroll All modal
   */
  public openEnrollAllModal() {
    this.modalService.open('multiple-operators');
  }

  /**
   * @param operatorsData
   * @description This function receives the operator data to be updated and dispatches an
   * action to enroll all operators on the policy
   */
  public enrollAll(operatorsData: Dictionary<KydCustomerDataModel>) {
    this.spinner.start();
    this.modalService.close('multiple-operators');

    const policyData: EnrollKydPolicyData = {
      policyNumber: this.kydPolicy.policyNumber,
      effectiveDate: this.kydPolicy.effectiveDate,
      endEffectiveDate: this.kydPolicy.endEffectiveDate,
      policyRisks: this.kydPolicy.policyRisks,
      policyRoles: this.kydPolicy.policyRoles,
      operatorData: operatorsData,
      enrollAll: true
    };

    this.store.dispatch(new KydPartialHouseholdActions.EnrollPolicy(policyData));
  }

  /**
   *
   * @description Opens the Unenroll All modal
   */
  public openUnenrollAllModal() {
    this.modalService.open('unenroll-modal');
  }

  /**
   * @description Dispatches an action to unenroll the Kyd Policy
   */
  public unEnrollAllOperators() {
    this.spinner.start();
    this.modalService.close('unenroll-modal');
    this.eligibleForUtah$
      .pipe(
        first(),
        tap(eligible =>
          this.store.dispatch(
            new KydPartialHouseholdActions.UnenrollPolicy({ eligibleForUtah: eligible })
          )
        )
      )
      .subscribe();

    //this.store.dispatch(new KydPartialHouseholdActions.UnenrollPolicy());
  }

  /**
   * @param operatorsData
   * @description This function receives the operator data to be updated and dispatches an
   * action to enroll a single operator. If the policy is not Opted In to KYD yet, dispatch
   * an action to EnrollPolicy, rather than EnrollOperator. The EnrollPolicy action will Opt In
   * the policy with only one operator enrolled.
   */
  public enrollOperator(operatorsData: any) {
    this.spinner.start();
    if (this.kydPolicy.enrollmentSummary.enrollmentStatus !== 'Enrolled') {
      const policyData: EnrollKydPolicyData = {
        policyNumber: this.kydPolicy.policyNumber,
        effectiveDate: this.kydPolicy.effectiveDate,
        endEffectiveDate: this.kydPolicy.endEffectiveDate,
        policyRisks: this.kydPolicy.policyRisks,
        policyRoles: this.kydPolicy.policyRoles,
        operatorData: operatorsData.operators,
        enrollAll: false,
        operatorName: operatorsData.operators[operatorsData.operatorId].fullName
      };
      this.store.dispatch(new KydPartialHouseholdActions.EnrollPolicy(policyData));
    } else {
      const enrollOperatorData: EnrollKydOperatorPayload = {
        policyNumber: this.kydPolicy.policyNumber,
        phoneNumber: operatorsData.operators[operatorsData.operatorId].phoneNumber,
        cdhId: operatorsData.operators[operatorsData.operatorId].customerId,
        operatorName: operatorsData.operators[operatorsData.operatorId].fullName
      };
      this.store.dispatch(new KydPartialHouseholdActions.EnrollOperator(enrollOperatorData));
    }
  }

  /**
   * @param operatorsData
   * @description Dispatches an action to unenroll the specified operator.
   */
  public unenrollOperator(operatorsData: any) {
    this.spinner.start();
    const unenrollOperatorData: EnrollKydOperatorPayload = {
      policyNumber: this.kydPolicy.policyNumber,
      phoneNumber: operatorsData.operators[operatorsData.operatorId].phoneNumber,
      cdhId: operatorsData.operators[operatorsData.operatorId].customerId,
      operatorName: operatorsData.operators[operatorsData.operatorId].fullName
    };
    this.store.dispatch(new KydPartialHouseholdActions.UnenrollOperator(unenrollOperatorData));
  }

  /**
   * @param operatorsData
   * @description Dispatches an action to update the phone number for the specified operator.
   */
  public updateOperatorPhoneNumber(operatorsData: any) {
    this.spinner.start();
    const updateOperatorPhoneData: UpdateKydOperatorPhonePayload = {
      operatorId: operatorsData.operatorId,
      phoneNumber: operatorsData.operators[operatorsData.operatorId].phoneNumber,
      cdhId: operatorsData.operators[operatorsData.operatorId].customerId
    };
    this.store.dispatch(
      new KydPartialHouseholdActions.UpdateOperatorPhoneNumber(updateOperatorPhoneData)
    );
  }

  public closeModal(modalId: string) {
    this.modalService.close(modalId);
  }

  ngOnDestroy() {
    this.spinner.stop();
    this.stop$.next();
    this.stop$.complete();
  }
}
